import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PluginListenerHandle } from '@capacitor/core';
import {
    ActionPerformed as LocalActionPerformed,
    LocalNotifications,
} from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import {
    ActionPerformed,
    PermissionStatus,
    PushNotificationSchema,
    PushNotifications,
    Token,
} from '@capacitor/push-notifications';
import {
    DeviceTokenRequest,
    LoansData,
    NotificationEventType,
    UserProfile,
    UserTopic,
    UserTopicsRequest,
    getEncryptionValueForLoan,
    getLoanNumbersFromProfile,
} from '@ld.consumer-portal/models';
import { FCMToken } from '../plugins/fcm-token.plugin';
import { AuthService } from './auth.service';
import { LoanSelectionService } from './loan-selection.service';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';
import { PushNotificationApiService } from './push-notification-api.service';

export interface NotificationData {
  route?: string;
  eventType?: string;
  loanNumber?: string;
  documentId?: string;
  [key: string]: unknown;
}

export interface PendingNotificationAction {
  eventType: NotificationEventType;
  loanNumber: string;
}

const EVENT_TYPE_ROUTE_MAP: Record<NotificationEventType, string> = {
  [NotificationEventType.AutoPayReminder]: 'autopay',
  [NotificationEventType.OneTimePaymentReminder]: 'payment',
};

@Injectable({ providedIn: 'root' })
export class PushNotificationService implements OnDestroy {
  private readonly platform = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly api = inject(PushNotificationApiService);
  private readonly logger = inject(LoggingService);
  private readonly authService = inject(AuthService);
  private readonly loanSelectionService = inject(LoanSelectionService);

  private static readonly TAG = 'PushNotification';

  private listeners: PluginListenerHandle[] = [];
  private fcmTokenEventListener: ((event: Event) => void) | null = null;
  private pendingFcmTokenResolve: ((token: string) => void) | null = null;

  private userContext: { globalUserId: string; userProfileId: string; firstName?: string } | null = null;
  private storedUserProfile: UserProfile | null = null;

  private readonly _token = signal<string | null>(null);
  private readonly _fcmToken = signal<string | null>(null);
  private readonly _permissionStatus = signal<PermissionStatus | null>(null);
  private readonly _userTopics = signal<UserTopic[]>([]);
  private readonly _cachedLoans = signal<LoansData[]>([]);
  private readonly _showOnboardingPrompt = signal(false);

  readonly token = this._token.asReadonly();
  readonly fcmToken = this._fcmToken.asReadonly();
  readonly permissionStatus = this._permissionStatus.asReadonly();
  readonly userTopics = this._userTopics.asReadonly();
  readonly cachedLoans = this._cachedLoans.asReadonly();
  readonly showOnboardingPrompt = this._showOnboardingPrompt.asReadonly();

  private static readonly PENDING_NOTIFICATION_KEY = 'ld_pending_notification_action';

  private readonly _pendingNotificationAction = signal<PendingNotificationAction | null>(null);
  readonly pendingNotificationAction = this._pendingNotificationAction.asReadonly();

  private log(method: string, message: string): void {
    this.logger.logTrace(`[${PushNotificationService.TAG}] ${method}: ${message}`);
  }

  private logError(method: string, message: string, error?: unknown): void {
    this.logger.logException(
      error instanceof Error ? error : new Error(`[${PushNotificationService.TAG}] ${method}: ${message}`),
    );
  }

  async initialize(): Promise<boolean> {
    if (!this.platform.isNativeMobile()) {
      return false;
    }

    this.setupFcmTokenEventListener();

    try {
      const permStatus = await PushNotifications.checkPermissions();
      this._permissionStatus.set(permStatus);
      if (permStatus.receive === 'prompt') {
        const result = await PushNotifications.requestPermissions();
        this._permissionStatus.set(result);

        if (result.receive !== 'granted') {
          return false;
        }
      } else if (permStatus.receive !== 'granted') {
        return false;
      }

      await this.setupListeners();
      await this.createLocalNotificationChannel();
      await PushNotifications.register();
      return true;
    } catch (error) {
      this.logError('initialize', 'failed', error);
      return false;
    }
  }

  async checkPermissionStatus(): Promise<'granted' | 'denied' | 'prompt' | null> {
    if (!this.platform.isNativeMobile()) {
      return null;
    }
    const status = await PushNotifications.checkPermissions();
    this._permissionStatus.set(status);
    return status.receive as 'granted' | 'denied' | 'prompt';
  }

  setUserContext(globalUserId: string, userProfileId: string, firstName?: string): void {
    this.userContext = { globalUserId, userProfileId, firstName };
  }

  setUserProfile(profile: UserProfile): void {
    this.storedUserProfile = profile;
  }

  clearUserContext(): void {
    this.userContext = null;
    this.storedUserProfile = null;
    this._userTopics.set([]);
    this._cachedLoans.set([]);
  }

  createLoansArrayAndCache(): LoansData[] {
    if (!this.storedUserProfile) {
      return [];
    }

    const loanNumbers = getLoanNumbersFromProfile(this.storedUserProfile);
    const loans: LoansData[] = [];

    for (const loanNumber of loanNumbers) {
      const encryptionValue = getEncryptionValueForLoan(this.storedUserProfile, loanNumber);
      if (encryptionValue) {
        loans.push({ loanNumber, authorizationEncryptionValue: encryptionValue });
      }
    }

    this._cachedLoans.set(loans);
    return loans;
  }

  invalidateCachedLoansArray(): void {
    this._cachedLoans.set([]);
  }

  async fetchAndCacheUserTopics(): Promise<boolean> {
    if (!this.userContext) {
      return false;
    }

    const loans = this._cachedLoans();
    const topics = await this.api.getUserTopicSubscriptions(this.userContext.userProfileId, loans);

    if (topics.length > 0) {
      this._userTopics.set(topics);
      return true;
    }

    const availableTopics = await this.api.getTopics();
    if (availableTopics.length > 0) {
      const defaultTopics: UserTopic[] = availableTopics.map((t) => ({
        ...t,
        enabled: false,
      }));
      this._userTopics.set(defaultTopics);
      return true;
    }

    return false;
  }

  async upsertFetchedUserTopics(isEnabled?: boolean): Promise<boolean> {
    const token = this._token();
    if (!this.userContext || !token) {
      return false;
    }

    let topics = this._userTopics();
    if (topics.length === 0) {
      return false;
    }

    if (isEnabled !== undefined) {
      topics = topics.map((t) => ({ ...t, enabled: isEnabled }));
      this._userTopics.set(topics);
    }

    const request: UserTopicsRequest = {
      deviceToken: token,
      updatedBy: this.userContext.firstName || '',
      loans: this._cachedLoans(),
      userTopics: topics,
    };

    return this.api.upsertUserTopicSubscriptions(this.userContext.userProfileId, request);
  }

  private static readonly PROMPT_SHOWN_KEY = 'ld_push_notification_prompt_shown';

  async onUserLogin(): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }

    try {
      const status = await this.checkPermissionStatus();

      if (status === 'granted') {
        await this.initialize();
      } else if (status === 'prompt') {
        const alreadyShown = localStorage.getItem(PushNotificationService.PROMPT_SHOWN_KEY);
        if (!alreadyShown) {
          this._showOnboardingPrompt.set(true);
        }
      }
    } catch (error) {
      this.logError('onUserLogin', 'failed', error);
    }
  }

  async handleOnboardingAllow(): Promise<boolean> {
    this._showOnboardingPrompt.set(false);
    localStorage.setItem(PushNotificationService.PROMPT_SHOWN_KEY, 'true');
    const initialized = await this.initialize();
    return initialized;
  }

  handleOnboardingDismiss(): void {
    this._showOnboardingPrompt.set(false);
    localStorage.setItem(PushNotificationService.PROMPT_SHOWN_KEY, 'true');
  }

  async registerDeviceAndTopics(): Promise<void> {
    const token = this._token();
    if (!token || !this.userContext) {
      return;
    }

    await this.registerTokenWithBackend(token);
    this.createLoansArrayAndCache();
    const fetched = await this.fetchAndCacheUserTopics();

    if (fetched) {
      await this.upsertFetchedUserTopics(true);
    }
  }

  async refreshLoansAndUpdateTopics(): Promise<void> {
    this.invalidateCachedLoansArray();
    this.createLoansArrayAndCache();
    const fetched = await this.fetchAndCacheUserTopics();
    if (fetched) {
      await this.upsertFetchedUserTopics();
    }
  }

  async unregister(): Promise<void> {
    this.clearUserContext();

    if (!this.platform.isNativeMobile()) {
      return;
    }

    try {
      await PushNotifications.removeAllDeliveredNotifications();
      this._token.set(null);
    } catch (error) {
      this.logError('unregister', 'failed', error);
    }
  }

  async getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
    if (!this.platform.isNativeMobile()) {
      return [];
    }
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  }

  async clearNotifications(): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }
    try {
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (error) {
      this.logError('clearNotifications', 'failed', error);
    }
  }

  private async setupListeners(): Promise<void> {
    if (this.listeners.length > 0) {
      return;
    }

    const registrationListener = await PushNotifications.addListener('registration', async (token: Token) => {
      this._token.set(token.value);

      await this.fetchAndStoreFcmToken();

      if (this.userContext) {
        await this.registerDeviceAndTopics();
      }
    });
    this.listeners.push(registrationListener);

    const errorListener = await PushNotifications.addListener('registrationError', (error) => {
      this.logError('onRegistration', 'registration error', error);
    });
    this.listeners.push(errorListener);

    const receivedListener = await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        this.handleNotificationReceived(notification);
      },
    );
    this.listeners.push(receivedListener);

    const actionListener = await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        this.handleNotificationAction(action);
      },
    );
    this.listeners.push(actionListener);

    const localActionListener = await LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (action: LocalActionPerformed) => {
        const data = action.notification.extra as NotificationData;
        if (data?.eventType && data?.loanNumber) {
          const eventType = this.parseEventType(data.eventType);
          if (eventType) {
            this.navigateByEventType(eventType, data.loanNumber);
            return;
          }
        }
        if (data?.route) {
          this.router.navigate([data.route], {
            queryParams: this.extractQueryParams(data),
          });
        }
      },
    );
    this.listeners.push(localActionListener);
  }

  private async fetchAndStoreFcmToken(): Promise<void> {
    if (!this.platform.isIOS() && !this.platform.isAndroid()) {
      return;
    }

    try {
      const existingToken = this._fcmToken();
      if (existingToken) {
        return;
      }

      // Android: Use FCMTokenPlugin directly for reliable token retrieval
      if (this.platform.isAndroid()) {
        try {
          const fcmResult = await FCMToken.getToken();
          if (fcmResult.token) {
            this._fcmToken.set(fcmResult.token);
            this.log('fetchAndStoreFcmToken', `Android FCM token retrieved via plugin`);
            // eslint-disable-next-line no-console
            console.log(`🔥 FCM TOKEN (use this for testing): ${fcmResult.token}`);
            return;
          }
        } catch (pluginError) {
          this.logError('fetchAndStoreFcmToken', 'FCMTokenPlugin failed, falling back', pluginError);
        }
      }

      // iOS: Wait for fcmTokenReceived event from AppDelegate
      if (this.platform.isIOS()) {
        const token = await this.waitForFcmTokenEvent(5000);
        if (token) {
          this._fcmToken.set(token);
          // eslint-disable-next-line no-console
          console.log(`🔥 FCM TOKEN (use this for testing): ${token}`);
          return;
        }
      }

      // Fallback: Read from Preferences (SharedPreferences on Android, UserDefaults on iOS)
      const result = await Preferences.get({ key: 'fcmToken' });
      if (result.value) {
        this._fcmToken.set(result.value);
        // eslint-disable-next-line no-console
        console.log(`🔥 FCM TOKEN from Preferences (use this for testing): ${result.value}`);
        return;
      }
    } catch (error) {
      this.logError('fetchAndStoreFcmToken', 'failed', error);
    }
  }

  private waitForFcmTokenEvent(timeoutMs: number): Promise<string | null> {
    return new Promise((resolve) => {
      const existingToken = this._fcmToken();
      if (existingToken) {
        resolve(existingToken);
        return;
      }

      const timeout = setTimeout(() => {
        const finalToken = this._fcmToken();
        if (finalToken) {
          resolve(finalToken);
        } else {
          resolve(null);
        }
        this.pendingFcmTokenResolve = null;
      }, timeoutMs);

      this.pendingFcmTokenResolve = (token: string) => {
        clearTimeout(timeout);
        this.pendingFcmTokenResolve = null;
        resolve(token);
      };
    });
  }

  private setupFcmTokenEventListener(): void {
    if (this.fcmTokenEventListener || (!this.platform.isIOS() && !this.platform.isAndroid())) {
      return;
    }

    this.fcmTokenEventListener = (event: Event) => {
      const customEvent = event as CustomEvent<{ token: string }>;
      const token = customEvent.detail?.token;
      if (token) {
        this._fcmToken.set(token);

        if (this.pendingFcmTokenResolve) {
          this.pendingFcmTokenResolve(token);
        }
      }
    };

    window.addEventListener('fcmTokenReceived', this.fcmTokenEventListener);
  }

  private removeFcmTokenEventListener(): void {
    if (this.fcmTokenEventListener) {
      window.removeEventListener('fcmTokenReceived', this.fcmTokenEventListener);
      this.fcmTokenEventListener = null;
    }
  }

  private async createLocalNotificationChannel(): Promise<void> {
    if (this.platform.isAndroid()) {
      try {
        await LocalNotifications.createChannel({
          id: 'ld-foreground-push',
          name: 'Push Notifications',
          importance: 5,
          sound: 'default',
          vibration: true,
        });
      } catch (error) {
        this.logError('createLocalNotificationChannel', 'failed', error);
      }
    }
  }

  private handleNotificationReceived(notification: PushNotificationSchema): void {
    // On Android, LDFirebaseMessagingService.showForegroundNotification() handles
    // displaying the notification natively. No need to schedule via LocalNotifications
    // here (that would cause duplicates). This handler is kept for logging/analytics.
    this.log(
      'handleNotificationReceived',
      `Foreground notification: ${notification.title ?? '(no title)'}`,
    );
  }

  private handleNotificationAction(action: ActionPerformed): void {
    const data = action.notification.data as NotificationData;

    if (data?.eventType && data?.loanNumber) {
      const eventType = this.parseEventType(data.eventType);
      if (eventType) {
        this.navigateByEventType(eventType, data.loanNumber);
        return;
      }
    }

    if (data?.route) {
      this.router.navigate([data.route], {
        queryParams: this.extractQueryParams(data),
      });
    }
  }

  private parseEventType(value: string): NotificationEventType | null {
    const values = Object.values(NotificationEventType) as string[];
    if (values.includes(value)) {
      return value as NotificationEventType;
    }
    return null;
  }

  private navigateByEventType(eventType: NotificationEventType, loanNumber: string): void {
    if (!this.authService.isAuthenticated()) {
      this.savePendingNotificationAction({ eventType, loanNumber });
      this.log('navigateByEventType', `User not authenticated, saved pending action: ${eventType} for loan ${loanNumber}`);
      return;
    }

    this.executeNotificationNavigation(eventType, loanNumber);
  }

  private executeNotificationNavigation(eventType: NotificationEventType, loanNumber: string): void {
    const routeSegment = EVENT_TYPE_ROUTE_MAP[eventType];
    if (!routeSegment) {
      this.log('executeNotificationNavigation', `No route mapping for event type: ${eventType}`);
      return;
    }

    const loanSelected = this.loanSelectionService.selectLoanById(loanNumber);
    if (!loanSelected) {
      this.log('executeNotificationNavigation', `Could not select loan: ${loanNumber}, navigating anyway`);
    }

    this.router.navigate(['/loans', loanNumber, routeSegment]);
    this.log('executeNotificationNavigation', `Navigated to /loans/${loanNumber}/${routeSegment}`);
  }

  executePendingNotificationAction(): boolean {
    const pending = this.loadPendingNotificationAction();
    if (!pending) {
      return false;
    }

    this.clearPendingNotificationAction();
    this.executeNotificationNavigation(pending.eventType, pending.loanNumber);
    return true;
  }

  private savePendingNotificationAction(action: PendingNotificationAction): void {
    this._pendingNotificationAction.set(action);
    try {
      sessionStorage.setItem(
        PushNotificationService.PENDING_NOTIFICATION_KEY,
        JSON.stringify(action),
      );
    } catch (error) {
      this.logError('savePendingNotificationAction', 'Failed to save pending action', error);
    }
  }

  private loadPendingNotificationAction(): PendingNotificationAction | null {
    const signalValue = this._pendingNotificationAction();
    if (signalValue) {
      return signalValue;
    }

    try {
      const stored = sessionStorage.getItem(PushNotificationService.PENDING_NOTIFICATION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as PendingNotificationAction;
        if (parsed.eventType && parsed.loanNumber) {
          return parsed;
        }
      }
    } catch (error) {
      this.logError('loadPendingNotificationAction', 'Failed to load pending action', error);
    }

    return null;
  }

  private clearPendingNotificationAction(): void {
    this._pendingNotificationAction.set(null);
    sessionStorage.removeItem(PushNotificationService.PENDING_NOTIFICATION_KEY);
  }

  private extractQueryParams(data: NotificationData): Record<string, string> {
    const params: Record<string, string> = {};

    if (data.loanNumber) {
      params['loanNumber'] = data.loanNumber;
    }
    if (data.documentId) {
      params['documentId'] = data.documentId;
    }

    return params;
  }

  private async registerTokenWithBackend(apnsToken: string): Promise<void> {
    if (!this.userContext) {
      return;
    }

    const deviceDetails = await this.platform.ensureDeviceDetails();
    if (!deviceDetails) {
      return;
    }

    let deviceToken = apnsToken;
    if (this.platform.isIOS() || this.platform.isAndroid()) {
      const fcmToken = this._fcmToken();
      if (fcmToken) {
        deviceToken = fcmToken;
      }
    }

    const request: DeviceTokenRequest = {
      deviceID: deviceDetails.deviceId,
      userProfileID: this.userContext.userProfileId,
      deviceToken: deviceToken,
      os: deviceDetails.platform === 'ios' ? 'iOS' : 'Android',
      appVersion: deviceDetails.appVersion,
      deviceType: deviceDetails.model,
      updatedBy: this.userContext.firstName,
      lastLogin: new Date().toISOString(),
    };

    await this.api.upsertUserDevice(this.userContext.globalUserId, request);
  }

  ngOnDestroy(): void {
    this.listeners.forEach((listener) => listener.remove());
    this.removeFcmTokenEventListener();
  }
}
